import { useEffect, useState } from 'react'
import { Select } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { SEARCH_DEBOUNCE_MS } from '@/shared/lib/localList'
import { searchDirectory, type DirectorySubscriber } from './cashdeskApi'

interface SubscriberPickerProps {
  value?: string | null
  onChange?: (value: string | null) => void
  onPick?: (subscriber: DirectorySubscriber | null) => void
  disabled?: boolean
  placeholder?: string
  style?: React.CSSProperties
}

export function SubscriberPicker({ value, onChange, onPick, disabled, placeholder, style }: SubscriberPickerProps) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [term, setTerm] = useState('')

  useEffect(() => {
    if (typed === term) return
    const timer = setTimeout(() => setTerm(typed.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [typed, term])

  const directory = useQuery({
    queryKey: queryKeys.cashdesk.directory(term),
    queryFn: ({ signal }) => searchDirectory(term, signal),
    enabled: term.length >= 2,
    staleTime: 15_000,
  })

  return (
    <Select<string>
      showSearch
      allowClear
      filterOption={false}
      value={value ?? undefined}
      disabled={disabled ?? false}
      onSearch={setTyped}
      onChange={(id) => {
        onChange?.(id ?? null)
        onPick?.((directory.data ?? []).find((row) => row.id === id) ?? null)
      }}
      loading={directory.isFetching}
      placeholder={placeholder ?? t('cashdesk.subscriber.searchPlaceholder')}
      notFoundContent={term.length < 2 ? t('cashdesk.subscriber.typeMore') : t('cashdesk.subscriber.notFound')}
      style={{ minWidth: 240, ...style }}
      options={(directory.data ?? []).map((row) => ({
        value: row.id,
        label: [row.login, row.displayName, row.customerId, row.phone].filter(Boolean).join(' · '),
      }))}
    />
  )
}
